import type {
  QueryClassification,
  QueryLabel,
} from './GlobalMapSearch.classify';
import { classifyQuery } from './GlobalMapSearch.classify';
import { CLASSIFY_PROMPT } from './GlobalMapSearch.prompt';
import { callAzureOpenAIChat } from './azureOpenAIChat';

const AI_TIMEOUT_MS = 5000;

const labelCache = new Map<string, QueryLabel[]>();

function buildClassification(
  labels: QueryLabel[],
  normalizedPhone: string | null,
): QueryClassification {
  const set = new Set(labels);
  return {
    labels,
    has: (label: QueryLabel) => set.has(label),
    normalizedPhone,
  };
}

/**
 * Classify the query into the agreed label set via Azure OpenAI. Returns
 * null on any failure mode so the caller can fall back to the heuristic.
 */
async function fetchAILabels(
  query: string,
  signal: AbortSignal,
): Promise<QueryLabel[] | null> {
  const content = await callAzureOpenAIChat(
    [
      { role: 'system', content: CLASSIFY_PROMPT },
      { role: 'user', content: `Query: "${query}"` },
    ],
    signal,
    { temperature: 0, maxTokens: 40, jsonMode: true, timeoutMs: AI_TIMEOUT_MS },
  );
  if (!content) return null;

  let parsed: { labels?: unknown };
  try {
    parsed = JSON.parse(content) as typeof parsed;
  } catch {
    return null;
  }

  const labels = parsed.labels;
  if (
    !Array.isArray(labels) ||
    labels.length !== 2 ||
    typeof labels[0] !== 'string' ||
    typeof labels[1] !== 'string'
  ) {
    return null;
  }
  return labels as QueryLabel[];
}

/**
 * Classify a search query via Azure OpenAI, with the synchronous heuristic
 * classifier as an instant fallback. `normalizedPhone` always comes from
 * the heuristic — the LLM doesn't compute it.
 */
export async function classifySearch(
  raw: string,
  signal: AbortSignal,
): Promise<QueryClassification> {
  const heuristic = classifyQuery(raw);

  const key = raw.trim().toLowerCase();
  if (!key) return heuristic;

  const cached = labelCache.get(key);
  if (cached) return buildClassification(cached, heuristic.normalizedPhone);

  const labels = await fetchAILabels(raw.trim(), signal);
  if (labels) {
    // eslint-disable-next-line no-console
    console.log(
      `[GlobalMapSearch AI] "${raw.trim()}" → [${labels.join(', ')}]`,
    );
    labelCache.set(key, labels);
    return buildClassification(labels, heuristic.normalizedPhone);
  }

  // eslint-disable-next-line no-console
  console.log(
    `[GlobalMapSearch AI] "${raw.trim()}" → fallback heuristic [${heuristic.labels.join(', ')}]`,
  );
  return heuristic;
}
